import { useEffect, useState } from 'react';
import { Download, MonitorDown, PlusSquare, Share, X } from 'lucide-react';
import type { Language } from '../../types';
import { _t } from '../../lib/i18n';
import { radiusSurfaceClass } from '../../styles/uiStandards';

const STORAGE_KEY = 'catalogofworks_pwa_install_dismissed';

type BeforeInstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
};

type InstallPWAProps = {
  lang: Language;
  appName?: string;
  /** Separate dismiss key so storefront and visiting-card prompts do not share state. */
  storageKey?: string;
};

function isStandaloneMode() {
  return (
    window.matchMedia('(display-mode: standalone)').matches
    || (window.navigator as Navigator & { standalone?: boolean }).standalone === true
  );
}

function isIos() {
  return /iphone|ipad|ipod/i.test(window.navigator.userAgent);
}

function isSafariDesktop() {
  const ua = window.navigator.userAgent;
  return /Macintosh|Windows/i.test(ua) && /Safari/i.test(ua) && !/Chrome|Chromium|Edg|OPR/i.test(ua);
}

const installHintPanelClass =
  'rounded-xl bg-[var(--bg-secondary)] p-3 text-xs leading-relaxed text-[var(--text-secondary)]';

export function InstallPWA({
  lang,
  appName,
  storageKey = STORAGE_KEY,
}: InstallPWAProps) {
  const [visible, setVisible] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);

  useEffect(() => {
    if (localStorage.getItem(storageKey) || isStandaloneMode()) {
      return;
    }

    const onBeforeInstallPrompt = (event: Event) => {
      event.preventDefault();
      setDeferredPrompt(event as BeforeInstallPromptEvent);
      setVisible(true);
    };

    window.addEventListener('beforeinstallprompt', onBeforeInstallPrompt);
    setVisible(true);

    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstallPrompt);
    };
  }, [storageKey]);

  const dismiss = () => {
    localStorage.setItem(storageKey, '1');
    setVisible(false);
    setDeferredPrompt(null);
  };

  const handleInstall = async () => {
    if (!deferredPrompt) return;
    await deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === 'accepted') {
      dismiss();
    }
  };

  if (!visible) {
    return null;
  }

  const installTitle = appName
    ? _t('pwa.installTitleNamed', lang).replace('{name}', appName)
    : _t('pwa.installTitle', lang);

  const body = deferredPrompt ? (
    <button
      type="button"
      onClick={() => void handleInstall()}
      className="flex w-full items-center justify-center gap-2 rounded-xl bg-[var(--accent-primary)] px-4 py-3 text-sm font-semibold text-white transition-transform active:scale-[0.98]"
    >
      <Download size={18} aria-hidden />
      {_t('pwa.installAction', lang)}
    </button>
  ) : isIos() ? (
    <p className={`flex flex-wrap items-center gap-1 ${installHintPanelClass}`}>
      {_t('pwa.iosStep1', lang)}
      <Share size={14} className="text-[var(--accent-primary)]" aria-hidden />
      {_t('pwa.iosStep2', lang)}
      <span className="font-semibold text-[var(--text-primary)]">{_t('pwa.iosStep3', lang)}</span>
      <PlusSquare size={14} aria-hidden />
    </p>
  ) : isSafariDesktop() ? (
    <p className={`flex items-start gap-2 ${installHintPanelClass}`}>
      <MonitorDown size={16} className="mt-0.5 shrink-0 text-[var(--accent-primary)]" aria-hidden />
      <span>{_t('pwa.desktopSafariHint', lang)}</span>
    </p>
  ) : /android/i.test(window.navigator.userAgent) ? (
    <p className={installHintPanelClass}>{_t('pwa.androidMenuHint', lang)}</p>
  ) : (
    <p className={`flex items-start gap-2 ${installHintPanelClass}`}>
      <MonitorDown size={16} className="mt-0.5 shrink-0 text-[var(--accent-primary)]" aria-hidden />
      <span>{_t('pwa.desktopChromiumHint', lang)}</span>
    </p>
  );

  return (
    <div
      className="fixed inset-x-4 bottom-4 z-[240] sm:inset-x-auto sm:end-4 sm:max-w-sm"
      role="region"
      aria-label={installTitle}
    >
      <div
        className={`${radiusSurfaceClass} border border-[var(--border-color)] bg-[var(--bg-primary)] p-4 shadow-2xl`}
      >
        <div className="mb-3 flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h3 className="text-sm font-semibold text-[var(--text-primary)]">
              {installTitle}
            </h3>
            <p className="mt-0.5 text-xs text-[var(--text-secondary)]">
              {_t('pwa.installSubtitle', lang)}
            </p>
          </div>
          <button
            type="button"
            onClick={dismiss}
            aria-label={_t('common.close', lang)}
            className="shrink-0 rounded-full p-1.5 text-[var(--text-secondary)] transition-colors hover:bg-[var(--bg-secondary)] hover:text-[var(--text-primary)]"
          >
            <X size={18} />
          </button>
        </div>

        {body}
      </div>
    </div>
  );
}
